/**
 * Activity history API layer (FS-058).
 *
 * One feed over two sources — audit entries the system recorded, and customer
 * contact a person logged. The backend unions them, so there is nothing to
 * merge here and no second request to make.
 *
 * `occurred_at`, not `created_at`: a call logged on Friday for Tuesday's
 * conversation belongs on Tuesday, and the feed sorts by when it happened.
 */

import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/api/client";

export type HistorySource = "audit" | "contact";

export interface HistoryEntry {
  id: string;
  source: HistorySource;
  occurred_at: string;
  actor_id: string | null;
  actor_name: string | null;
  action: string;
  entity_type: string;
  /** Null for a logged note that is not attached to anything. */
  entity_id: string | null;
  entity_label: string;
  changes: Record<string, unknown> | null;
  extra_data: Record<string, unknown> | null;
}

export interface HistoryPage {
  items: HistoryEntry[];
  total: number;
  page: number;
  page_size: number;
  pages: number;
  /** What these filters selected, in words. Shown, not just carried. */
  counted: string;
}

export interface HistoryKind {
  key: string;
  label: string;
  /** Detail-page path, supplied by the server so no route table is duplicated here. */
  route: string | null;
  count: number;
}

export interface HistoryFacets {
  entity_types: HistoryKind[];
  actors: { id: string; name: string }[];
  actions: string[];
  contact_kinds: string[];
  sources: HistorySource[];
}

export interface FeedFilters {
  page?: number;
  pageSize?: number;
  entityTypes?: string[];
  actions?: string[];
  sources?: HistorySource[];
  actorId?: string;
  since?: string;
  until?: string;
  search?: string;
}

export function toParams(filters: FeedFilters): URLSearchParams {
  const sp = new URLSearchParams();
  sp.set("page", String(filters.page ?? 1));
  sp.set("page_size", String(filters.pageSize ?? 50));
  // Repeatable params rather than a comma-joined string, so a value containing
  // a comma can never split into two filters.
  filters.entityTypes?.forEach((value) => sp.append("entity_type", value));
  filters.actions?.forEach((value) => sp.append("action", value));
  filters.sources?.forEach((value) => sp.append("source", value));
  if (filters.actorId) sp.set("actor_id", filters.actorId);
  if (filters.since) sp.set("since", filters.since);
  if (filters.until) sp.set("until", filters.until);
  if (filters.search) sp.set("search", filters.search);
  return sp;
}

export function useActivityFeed(filters: FeedFilters = {}) {
  return useQuery<HistoryPage>({
    queryKey: ["activity", "feed", filters],
    queryFn: async () =>
      (await apiClient.get(`/api/v1/activity/feed?${toParams(filters)}`))
        .data as HistoryPage,
  });
}

export function useActivityFacets() {
  return useQuery<HistoryFacets>({
    queryKey: ["activity", "facets"],
    queryFn: async () =>
      (await apiClient.get("/api/v1/activity/facets")).data as HistoryFacets,
  });
}

export function useEntityTimeline(
  entityType: string,
  entityId: string | undefined,
  page = 1,
) {
  return useQuery<HistoryPage>({
    queryKey: ["activity", "entity", entityType, entityId, page],
    queryFn: async () =>
      (
        await apiClient.get(
          `/api/v1/activity/entity/${entityType}/${entityId}?page=${page}`,
        )
      ).data as HistoryPage,
    enabled: !!entityId,
  });
}
